import type { ChatMessageType } from '@kanbu/schema';
import type { ContentBlock } from '@kanbu/schema/contracts';
import { ChatRole, MessageGenerationStatus } from '@kanbu/schema/enums';
import { sortMessagesChronologically } from '@kanbu/shared';

/** Gemini Live can finish a turn before its tool result arrives. */
export function mergeToolResultMessages(
  messages: ChatMessageType[],
): ChatMessageType[] {
  const calls = new Map<string, ChatMessageType>();
  const updatedBlocks = new Map<ChatMessageType, ContentBlock[]>();

  const editableBlocks = (message: ChatMessageType): ContentBlock[] => {
    let blocks = updatedBlocks.get(message);

    if (!blocks) {
      blocks = [...(message.blocks ?? [])];
      updatedBlocks.set(message, blocks);
    }

    return blocks;
  };

  const assistantMessages = messages
    .filter(message => message.role === ChatRole.Assistant)
    .sort(sortMessagesChronologically);

  assistantMessages.forEach(message => {
    for (const block of message.blocks ?? []) {
      if (block.type === 'tool_call' && block.id) {
        calls.set(block.id, message);
      }
    }

    for (const block of message.blocks ?? []) {
      if (block.type !== 'tool_result') continue;

      const owner = calls.get(block.tool_call_id);

      if (!owner || owner === message) continue;

      const ownerBlocks = editableBlocks(owner);
      const callIndex = ownerBlocks.findIndex(
        candidate =>
          candidate.type === 'tool_call' && candidate.id === block.tool_call_id,
      );

      ownerBlocks.splice(callIndex + 1, 0, block);

      const resultBlocks = editableBlocks(message);
      resultBlocks.splice(resultBlocks.indexOf(block), 1);
    }
  });

  return messages.flatMap(message => {
    const blocks = updatedBlocks.get(message);

    if (
      blocks?.length === 0 &&
      message.generationStatus !== MessageGenerationStatus.Stopped &&
      message.generationStatus !== MessageGenerationStatus.Failed
    ) {
      return [];
    }

    return [blocks ? { ...message, blocks } : message];
  });
}
